import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import IFlightRangeData from '../types/FlightRanges';
import FlightRangeForm from '../Components/FlightRange/FlightRangeForm';
import FlightRangeModifyTable from '../Components/FlightRange/FlightRangeModifyTable';
import FlightRangeFooterActions from '../Components/FlightRange/FlightRangeFooterActions';
import { useFlightTypeDescriptions } from '../hooks/useFlightTypeDescriptions';
import { useCarrierData } from '../hooks/useCarrierData';
import ConfirmationModal from '../Components/Modals/ConfirmationModal';
import NotificationModal from '../Components/Modals/NotificationModal';
import './FlightRangeModify.scss';
import './FlightRangeModify.loading.scss';
import '../Components/FlightRange/FlightTypeDropdown.scss';
import { useAuth } from 'react-oidc-context';
import FlightRangeService from '../services/FlightRangeService';
import IModifyFlightRangeRequest from '../types/ModifyFlightRangeRequest';
import IFlightRangeRequest from '../types/FlightRangeRequest';
import IDraftData from '../types/DraftData';
import IDraftRequest from '../types/DraftRequest';
import { calculateDiscontinueDates, getToday } from '../utils/dateUtils';
import DraftService from '../services/DraftService';
import IReplacementRange from '../types/ReplacementRange';
import { NotificationData } from '../types/NotificationData';
import { SetupNotificationDefaults, parseEmailAddresses } from '../utils/emailUtils';
import { EnvConfigs } from '../http-common';
import NotificationService from '../services/NotificationService';
import { CreateNotificationRequest } from '../types/CreateNotificationRequest';
import "@aurodesignsystem/auro-loader";

interface LocationState {
  selectedFlightRanges: (IFlightRangeData & {
    operatingCarrierId?: string;
  })[];
  replacementFlightRanges?: IReplacementRange[];
  draftId?: number;
}

interface FormSnapshot {
  flightRangeStart: string;
  flightRangeEnd: string;
  operatingCarrier: string;
  flightType: string;
  effectiveDate: string;
  wetLease: string;
}

/**
 * Utility function to format flight numbers into start-end ranges
 */
const formatFlightNumberRanges = (flightNumbers: number[]): string => {
  if(flightNumbers.length === 0) return '';

  const sortedFlightNumbers = [...flightNumbers].sort((a, b) => a - b);

  //Identify continuous ranges
  const continuousRanges: [number, number][] = [];
  let rangeStart = sortedFlightNumbers[0];
  let rangeEnd = sortedFlightNumbers[0];

  for (let i = 1; i < sortedFlightNumbers.length; i++) {
    if(sortedFlightNumbers[i] === rangeEnd + 1) {
      rangeEnd = sortedFlightNumbers[i];
    } else {
      continuousRanges.push([rangeStart, rangeEnd]);
      rangeStart = sortedFlightNumbers[i];
      rangeEnd = sortedFlightNumbers[i];
    }
  }

  continuousRanges.push([rangeStart,  rangeEnd]);

  return continuousRanges.map(([start, end]) => {
    if (start === end) {
      return `${start}`;
    } else {
      return `${start} to ${end}`;
    }
  }).join(', ');
}

/**
 * Utility function to check flight range coverage and overlaps
 */
const analyzeFlightRangeCoverage = (
  selectedRanges: IFlightRangeData[],
  replacementRanges: IReplacementRange[]
) => {
  // Get all flight numbers from selected ranges
  const selectedFlightNumbers = new Set<number>();
  selectedRanges.forEach(range => {
    for (let i = range.start; i <= range.end; i++) {
      selectedFlightNumbers.add(i);
    }
  });

  // Check which flight numbers are covered by replacement ranges
  const coveredFlightNumbers = new Set<number>();
  const flightNumberOccurrences = new Map<number, number>();

  replacementRanges.forEach(range => {
    for (let i = range.start; i <= range.end; i++) {
      coveredFlightNumbers.add(i);
      flightNumberOccurrences.set(i, (flightNumberOccurrences.get(i) ?? 0) + 1);
    }
  });

  // Find flight numbers that are not covered
  const uncoveredFlightNumbers: number[] = [];
  selectedFlightNumbers.forEach(num => {
    if (!coveredFlightNumbers.has(num)) {
      uncoveredFlightNumbers.push(num);
    }
  });

  // Find overlapping flight numbers
  const overlappingFlightNumbers: number[] = [];
  flightNumberOccurrences.forEach((count, num) => {
    if (count > 1 && selectedFlightNumbers.has(num)) {
      overlappingFlightNumbers.push(num);
    }
  });

  return {
    uncoveredFlightNumbers,
    overlappingFlightNumbers,
    isFullyCovered: uncoveredFlightNumbers.length === 0,
    hasNoOverlaps: overlappingFlightNumbers.length === 0
  };
};

/**
 * Main component for modifying flight ranges
 */
const FlightRangeModify: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;

  // Data state - moved hooks to the top to ensure proper React hooks order
  const { carrierOptions, getCarrierIdByCode, getCarrierCodeById } = useCarrierData();
  const { flightTypeOptions, isLoading, getDescriptionByFlightTypeId, getServiceTypeByFlightTypeId } = useFlightTypeDescriptions();



  // Form state
  const [flightRangeStart, setFlightRangeStart] = useState<string>('');
  const [flightRangeEnd, setFlightRangeEnd] = useState<string>('');
  const [operatingCarrier, setOperatingCarrier] = useState<string>('');


  const [flightType, setFlightType] = useState<string>('');
  const [effectiveDate, setEffectiveDate] = useState<string>('');
  const [wetLease, setWetLease] = useState<string>('');

  // Data state
  const [replacementRanges, setReplacementRanges] = useState<IReplacementRange[]>([]);
  const [draftId, setDraftId] = useState<number | undefined>(undefined);
  const [currentSelectedRanges, setCurrentSelectedRanges] = useState<IFlightRangeData[]>([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [notificationData, setNotificationData] = useState<NotificationData>(
    SetupNotificationDefaults(EnvConfigs.notificationEmail)
  );
  const [rangesCoverage, setRangesCoverage] = useState<{
    isFullyCovered: boolean;
    hasNoOverlaps: boolean;
    uncoveredFlightNumbers: number[];
    overlappingFlightNumbers: number[];
    boundsValidationError?: string;
  }>({
    isFullyCovered: false,
    hasNoOverlaps: true,
    uncoveredFlightNumbers: [],
    overlappingFlightNumbers: [],
    boundsValidationError: undefined
  });

  // References to track form state
  const lastSubmittedValues = useRef<FormSnapshot | null>(null);
  const formInitializedRef = useRef<boolean>(false);

  // Initialize form data from selected ranges
  useEffect(() => {
    if (state?.selectedFlightRanges) {
      setCurrentSelectedRanges([...state.selectedFlightRanges]);
    }
    if (state?.replacementFlightRanges) {
      const replacementRanges = state.replacementFlightRanges;
      // replace id with unique id
      replacementRanges.forEach(range => {
        range.id = `replacement-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      });
      setReplacementRanges(replacementRanges);
    }
    if (state?.draftId) {
      setDraftId(state.draftId);
    }
  }, [state]);

  // Update coverage analysis whenever replacement ranges or selected ranges change
  useEffect(() => {
    if (currentSelectedRanges.length > 0 && replacementRanges.length > 0) {
      setRangesCoverage(prev => ({
        ...analyzeFlightRangeCoverage(currentSelectedRanges, replacementRanges),
        boundsValidationError: prev.boundsValidationError
      }));
    } else {
      setRangesCoverage(prev => ({
        isFullyCovered: false,
        hasNoOverlaps: true,
        uncoveredFlightNumbers: [],
        overlappingFlightNumbers: [],
        boundsValidationError: prev.boundsValidationError
      }));
    }
  }, [replacementRanges, currentSelectedRanges]);

  // Find matching flight type and initialize form when data is loaded
  useEffect(() => {
    // Only initialize the form once when both data sources are available
    // and the form hasn't been initialized yet
    if (currentSelectedRanges.length > 0 && flightTypeOptions.length > 0 && !formInitializedRef.current) {
      const firstRange = currentSelectedRanges[0];

      // Find the matching flight type from the API data
      // Try to find an exact match first
      let matchingOption = flightTypeOptions.find(
        option => option.value === firstRange.flightTypeDescription
      );

      // If no exact match, try a case-insensitive match
      if (!matchingOption) {
        matchingOption = flightTypeOptions.find(
          option => option.value.toLowerCase() ===
            (firstRange.flightTypeDescription ? firstRange.flightTypeDescription.toLowerCase() : '')
        );
      }

      setFlightRangeStart(String(firstRange.start || ''));
      setFlightRangeEnd(String(firstRange.end || ''));

      if (firstRange.operatingCarrier) {
        setOperatingCarrier(firstRange.operatingCarrier.trim());
      }

      if (matchingOption) {
        // Store the flight type description as before
        setFlightType(matchingOption.value);
      }

      setEffectiveDate(firstRange.effectiveDate || '');

      // Mark the form as initialized
      formInitializedRef.current = true;
    }
  }, [currentSelectedRanges, flightTypeOptions]);

  // Redirect to flight ranges list if no ranges are selected
  useEffect(() => {
    if (!state || !state.selectedFlightRanges || state.selectedFlightRanges.length === 0) {
      navigate('/flight-ranges', { replace: true });
    }
  }, [state, navigate]);

  const auth = useAuth();

  // Early return if no state
  if (!state || !state.selectedFlightRanges) {
    return null;
  }

  // Handler functions
  const handleCancel = () => {
    navigate('/flight-ranges/admin');
  };

  const handleRemoveRange = (id: string) => {
    // Update the current selected ranges by removing the one with the matching ID
    const updatedRanges = currentSelectedRanges.filter(range => range.id !== id);
    setCurrentSelectedRanges(updatedRanges);

    // If all selected ranges are removed, we should go back to the flight ranges page
    if (updatedRanges.length === 0) {
      navigate('/flight-ranges/admin');
    }
  };

  const handleRemoveReplacementRange = (id: string) => {
    // Remove the replacement range with the matching ID
    const updatedReplacementRanges = replacementRanges.filter(range => range.id !== id);

    setReplacementRanges(updatedReplacementRanges);

    // Check if this will cause coverage issues and update the analysis
    if (currentSelectedRanges.length > 0 && updatedReplacementRanges.length > 0) {
      setRangesCoverage(prev => ({
        ...analyzeFlightRangeCoverage(currentSelectedRanges, updatedReplacementRanges),
        boundsValidationError: prev.boundsValidationError
      }));
    } else {
      setRangesCoverage(prev => ({
        isFullyCovered: false,
        hasNoOverlaps: true,
        uncoveredFlightNumbers: [],
        overlappingFlightNumbers: [],
        boundsValidationError: prev.boundsValidationError
      }));
    }
  };

  const hasFormChanged = (currentValues: FormSnapshot, lastValues: FormSnapshot | null): boolean => {
    if (!lastValues) return true;

    return (
      currentValues.flightRangeStart !== lastValues.flightRangeStart ||
      currentValues.flightRangeEnd !== lastValues.flightRangeEnd ||
      currentValues.operatingCarrier !== lastValues.operatingCarrier ||
      currentValues.flightType !== lastValues.flightType ||
      currentValues.effectiveDate !== lastValues.effectiveDate ||
      currentValues.wetLease !== lastValues.wetLease
    );
  };

  const handleUpdate = () => {
    // Validate required fields before adding replacement row
    if (!flightType || !effectiveDate) {
      alert('Please fill in all required fields (Flight Type and Effective Date) before updating.');
      return;
    }

    if (!wetLease) {
      alert('Please select Yes or No for the wet lease option before updating.');
      return;
    }

    const currentFormValues: FormSnapshot = {
      flightRangeStart,
      flightRangeEnd,
      operatingCarrier,
      flightType,
      effectiveDate,
      wetLease
    };

    // Check if form values have changed since last submission
    if (!hasFormChanged(currentFormValues, lastSubmittedValues.current)) {
      alert('No changes detected. Please modify at least one field before updating.');
      return;
    }

    // Use the flightType directly as it now contains the description
    const selectedFlightTypeDescription = flightType;

    // Find the matching flight type option to get its ID
    const matchingOption = flightTypeOptions.find(option => option.value === flightType);
    const flightTypeId = matchingOption?.id;

    // Calculate the bounds of the original selected ranges
    const originalMinStart = Math.min(...currentSelectedRanges.map(r => r.start));
    const originalMaxEnd = Math.max(...currentSelectedRanges.map(r => r.end));

    // Create a new replacement range with current form values
    const start = parseInt(flightRangeStart) || originalMinStart;
    const end = parseInt(flightRangeEnd) || originalMaxEnd;

    // Validate that the replacement range stays within the bounds of the original selected ranges
    if (start < originalMinStart || end > originalMaxEnd) {
      const originalRangesText = currentSelectedRanges
        .map(r => `${r.start} - ${r.end}`)
        .join(', ');
      const errorMessage = `Replacement flight range (${start} - ${end}) must be within the bounds of the original selected range(s): ${originalRangesText}. Please adjust the start and end numbers.`;

      setRangesCoverage(prev => ({
        ...prev,
        boundsValidationError: errorMessage
      }));
      return;
    }

    // Additional validation: start must be less than or equal to end
    if (start > end) {
      const errorMessage = 'Start flight number must be less than or equal to end flight number.';
      setRangesCoverage(prev => ({
        ...prev,
        boundsValidationError: errorMessage
      }));
      return;
    }

    // Clear any previous bounds validation errors if validation passes
    setRangesCoverage(prev => ({
      ...prev,
      boundsValidationError: undefined
    }));

    // Each replacement range gets a unique ID based on timestamp
    const uniqueId = `replacement-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    // Determine the correct operating carrier and its ID
    const currentOperatingCarrier = operatingCarrier || (currentSelectedRanges[0].operatingCarrier ? currentSelectedRanges[0].operatingCarrier.trim() : '');

    // Use dynamic lookup based on the selected operating carrier
    // This will work correctly even if the user changed the operating carrier
    const operatingCarrierId = getCarrierIdByCode(currentOperatingCarrier);

    const wetLeaseIndicator = currentFormValues.wetLease.toLowerCase() === 'yes';

    // Preserve the marketing carrier from the original selected ranges
    // Use the first selected range's marketing carrier
    const preservedMarketingCarrier = currentSelectedRanges[0]?.marketingCarrier || 'AS';

    const newReplacementRange: IReplacementRange = {
      id: uniqueId, // Use the generated unique ID for the replacement range
      marketingCarrier: preservedMarketingCarrier,
      start: start,
      end: end,
      flightType: flightType,
      flightTypeDescription: selectedFlightTypeDescription,
      flightTypeId: flightTypeId, // Include the ID from the FlightTypeInfo API
      effectiveDate: effectiveDate,
      operatingCarrier: currentOperatingCarrier,
      operatingCarrierId: operatingCarrierId, // Dynamically looked up based on the current operating carrier
      wetLease: wetLeaseIndicator,
      discontinueDate: undefined
    };

    // Add the new replacement range to the existing list
    setReplacementRanges(prevRanges => [...prevRanges, newReplacementRange]);

    // Update the last submitted values
    lastSubmittedValues.current = { ...currentFormValues };
  };

  const handleReset = () => {
    setFlightRangeStart('');
    setFlightRangeEnd('');
    setOperatingCarrier('');
    setFlightType('');
    setEffectiveDate('');
    setWetLease('');
    setReplacementRanges([]); // Clear all replacement ranges on reset
    formInitializedRef.current = false; // Allow the form to be initialized again
    lastSubmittedValues.current = null; // Reset the last submitted values
    // Reset selected ranges to original state
    if (state?.selectedFlightRanges) {
      setCurrentSelectedRanges([...state.selectedFlightRanges]);
    }
  };

  const handleSaveDraft = () => {
    if (replacementRanges.length === 0) {
      alert('Please create at least one replacement flight range before proceeding.');
      return;
    }

    // Analyze flight range coverage and overlaps
    const {
      uncoveredFlightNumbers,
      overlappingFlightNumbers,
      isFullyCovered,
      hasNoOverlaps
    } = analyzeFlightRangeCoverage(currentSelectedRanges, replacementRanges);

    // Check for uncovered flight numbers
    if (!isFullyCovered) {
      alert(`Missing ranges: ${formatFlightNumberRanges(uncoveredFlightNumbers)}`);
      return;
    }

    // Check for overlaps in replacement ranges
    if (!hasNoOverlaps) {
      alert(`Overlapping ranges: ${formatFlightNumberRanges(overlappingFlightNumbers)}`);
      return;
    }

    // make api call to save draft here
    // For the backend API call, we'd provide:
    // 1. The ID of the flight range (range.id)
    // 2. The updated details for the flight range
    // Prepare data for the API call
    const modifiedRanges:IDraftData[] = replacementRanges.map(range => ({
      id: draftId || 0,
      flightRangeId: 0, // New range has to be 0 for the API regardless of our frontend ID
      flightRangeStart: range.start,
      flightRangeStop: range.end,
      marketingCarrierId: 6, // Always AS
      operatingCarrierId: Number(range.operatingCarrierId),
      wetLease: range.wetLease,
      flightTypeId: Number(range.flightTypeId),
      effectiveDate: range.effectiveDate,
      discontinueDate: undefined, // New ranges do not have a discontinue date
      modifiedDate: getToday(),
      modifiedBy: '' // Placeholder, not used in modify
    }));

    const existingRanges:IDraftData[] = currentSelectedRanges.map(range => ({
      id: draftId || 0,
      flightRangeId: Number(range.id),
      flightRangeStart: range.start,
      flightRangeStop: range.end,
      marketingCarrierId: 6, // Always AS
      operatingCarrierId: Number(getCarrierIdByCode(range.operatingCarrier ? range.operatingCarrier.trim() : '')) || -1,
      wetLease: false, // Placeholder, not used in modify
      flightTypeId: Number(flightTypeOptions.find(option => option.value === range.flightTypeDescription)?.id) || -1,
      effectiveDate: range.effectiveDate,
      discontinueDate: undefined, // Don't need to assign discontinue date when saving draft
      modifiedDate: getToday(),
      modifiedBy: '' // Placeholder, not used in modify
    }));

    const requestBody:IDraftRequest  = {
      drafts: [...existingRanges, ...modifiedRanges]
    };

    if (draftId) {
      // make an API call here to modify draft
      const modifyDraftResult = async () => {
        try {
          const token = auth.user?.access_token;
          if (!token) {
            throw new Error('Authentication token not found');
          }
          await DraftService.modify(requestBody, token);

          // Navigate back to the flight ranges page with a success message
          navigate('/flight-ranges/admin/drafts', {
            state: {
              success: true
            }
          });
        } catch (err) {
          console.error('Error modifying draft:', err);
          const errorMessage = err instanceof Error && err.message === 'Authentication token not found'
            ? 'You must be authenticated to modify draft.'
            : 'An error occurred while modifying draft. Please try again.';
          alert(errorMessage);
        }
      };

      modifyDraftResult();
      return;
    }

    // make an API call here to save new draft
    const createDraftResult = async () => {
      try {
        const token = auth.user?.access_token;
        if (!token) {
          throw new Error('Authentication token not found');
        }
        await DraftService.create(requestBody, token);

        // Navigate back to the flight ranges page with a success message
        navigate('/flight-ranges/admin/drafts', {
          state: {
            success: true
          }
        });
      } catch (err) {
        console.error('Error creating new draft:', err);
        const errorMessage = err instanceof Error && err.message === 'Authentication token not found'
          ? 'You must be authenticated to create new draft.'
          : 'An error occurred while creating new draft. Please try again.';
        alert(errorMessage);
      }
    };

    createDraftResult();
  };

  const handleEditNotification = () => {
    setIsConfirmationModalOpen(false);
    setIsNotificationModalOpen(true);
  };

  const handleNotificationConfirmed = (emailData: NotificationData) => {
    // Store notification data and return to confirmation modal
    setNotificationData(emailData);
    setIsNotificationModalOpen(false);
    setIsConfirmationModalOpen(true);
  };

  const handleConfirmModify = async () => {

    setIsConfirmationModalOpen(false);
    setIsSubmitting(true);

    // Create a map of replacement ranges for each selected range
    const replacementRangesMap: Record<string, any[]> = {};

    // Group replacement ranges by their source range ID
    // We need to determine which replacement ranges belong to which original ranges
    replacementRanges.forEach(range => {
      // Get the original range that this replacement range is covering
      const overlappingOriginalRanges = currentSelectedRanges.filter(
        originalRange =>
          // If there's any overlap between this range and the original range
          (range.start <= originalRange.end && range.end >= originalRange.start)
      );

      // For each overlapping original range, add this replacement range to its list
      overlappingOriginalRanges.forEach(originalRange => {
        const originalId = originalRange.id;
        if (!replacementRangesMap[originalId]) {
          replacementRangesMap[originalId] = [];
        }
        replacementRangesMap[originalId].push(range);
      });
    });

    // Calculate discontinue date for each existing flight range based on the minimum effective date
    // of its replacement ranges
    const discontinueDates = calculateDiscontinueDates(replacementRangesMap);

    // Check if we have valid discontinue dates for all ranges
    const missingDates = currentSelectedRanges
      .map(range => range.id)
      .filter(id => !discontinueDates[id]);

    if (missingDates.length > 0) {
      alert(`Could not calculate discontinue date. Please provide a valid effective date for all replacement ranges.`);
      return;
    }

    // For the backend API call, we'd provide:
    // 1. The ID of the flight range (range.id)
    // 2. The updated details for the flight range
    // Prepare data for the API call
    const modifiedRanges:IFlightRangeRequest[] = replacementRanges.map(range => ({
      id: 0, // New range has to be 0 for the API regardless of our frontend ID
      start: range.start,
      end: range.end,
      marketingCarrierId: 6, // Always AS
      operatingCarrierId: Number(range.operatingCarrierId),
      wetLease: range.wetLease,
      flightTypeId: Number(range.flightTypeId),
      effectiveDate: range.effectiveDate,
      discontinueDate: undefined // New ranges do not have a discontinue date
    }));
 
    const existingRanges:IFlightRangeRequest[] = currentSelectedRanges.map(range => ({
      id: Number(range.id),
      start: range.start,
      end: range.end,
      marketingCarrierId: 6, // Always AS
      operatingCarrierId: Number(getCarrierIdByCode(range.operatingCarrier?.trim() || '')) || -1,
      wetLease: false, // Existing ranges don't have wetLease data in IFlightRangeData
      flightTypeId: Number(flightTypeOptions.find(option => option.value === range.flightTypeDescription)?.id) || -1,
      effectiveDate: range.effectiveDate,
      discontinueDate: discontinueDates[range.id] // Use the correct discontinue date for each range
    }));
 
    const modifyRequestBody: IModifyFlightRangeRequest = {
      existingFlightRanges: existingRanges,
      newFlightRanges: modifiedRanges
    };

    // make an API call here to submit the modifications with the original IDs
    try {
      const token = auth.user?.access_token;
      if (!token) {
        throw new Error('Authentication token not found');
      }

      await FlightRangeService.modifyFlightRanges(token, modifyRequestBody);

      let notificationSent = false;

      // Send notification email if feature is enabled and notification data is present
      // Email validation already done in handleModify before opening the modal
      if (EnvConfigs.enableNotificationWorkflow && notificationData) {
        const toEmails = parseEmailAddresses(notificationData.to);

        if (toEmails.length === 0) {
          console.warn('No recipients specified for notification');
        } else {
          try {
            // Build enriched email data with descriptive values
            const emailExistingRanges = existingRanges.map(range => ({
              range: `${range.start} - ${range.end}`,
              marketingCarrier: 'AS', // Always Alaska Airlines
              operatingCarrier: getCarrierCodeById(range.operatingCarrierId) || `ID: ${range.operatingCarrierId}`,
              wetLease: range.wetLease ? 'Yes' : 'No',
              serviceType: getServiceTypeByFlightTypeId(range.flightTypeId),
              flightType: getDescriptionByFlightTypeId(range.flightTypeId) || `ID: ${range.flightTypeId}`,
              effectiveDate: range.effectiveDate || 'N/A',
              discontinueDate: range.discontinueDate || 'N/A'
            }));

            const emailNewRanges = modifiedRanges.map(range => ({
              range: `${range.start} - ${range.end}`,
              marketingCarrier: 'AS', // Always Alaska Airlines
              operatingCarrier: getCarrierCodeById(range.operatingCarrierId) || `ID: ${range.operatingCarrierId}`,
              wetLease: range.wetLease ? 'Yes' : 'No',
              serviceType: getServiceTypeByFlightTypeId(range.flightTypeId),
              flightType: getDescriptionByFlightTypeId(range.flightTypeId) || `ID: ${range.flightTypeId}`,
              effectiveDate: range.effectiveDate || 'N/A',
              discontinueDate: range.discontinueDate || 'N/A'
            }));

            // Call /api/Notify which generates PDF and sends email
            const notificationRequest: CreateNotificationRequest = {
              message: {
                from: notificationData.from,
                to: toEmails,
                cc: parseEmailAddresses(notificationData.cc),
                subject: notificationData.subject,
                appUrl: window.location.origin,
                userText: notificationData.userText
              },
              flightRanges: modifyRequestBody,
              emailData: {
                existingFlightRanges: emailExistingRanges,
                newFlightRanges: emailNewRanges
              }
            };

            await NotificationService.createNotification(notificationRequest, token);
            notificationSent = true;
          } catch (notifyErr) {
            // Log notification errors but don't block the user flow
            console.error('Notification error:', notifyErr);

            // Extract more detailed error information for logging
            if (notifyErr && typeof notifyErr === 'object' && 'response' in notifyErr) {
              const axiosError = notifyErr as any;
              const status = axiosError.response?.status;
              console.error(`Notification failed with status ${status}`);
            }
          }
        }
      }

      // Navigate back to the flight ranges admin page with success flags
      navigate('/flight-ranges/admin', {
        state: {
          success: true,
          modificationSuccess: true,
          notificationSent: notificationSent
        }
      });
    } catch (err) {
      console.error('Error modifying flight ranges:', err);
      const errorMessage = err instanceof Error && err.message === 'Authentication token not found'
        ? 'You must be authenticated to modify flight ranges.'
        : 'An error occurred while modifying flight ranges. Please try again.';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModify = () => {
    if (replacementRanges.length === 0) {
      alert('Please create at least one replacement flight range before proceeding.');
      return;
    }

    if (!wetLease) {
      alert('Please select Yes or No for the wet lease option before proceeding.');
      return;
    }

    // Check if at least one recipient is provided (if notification workflow is enabled)
    if (EnvConfigs.enableNotificationWorkflow && notificationData) {
      const toEmails = parseEmailAddresses(notificationData.to);
      if (toEmails.length === 0) {
        alert('Please provide at least one recipient email address in the "To" field.');
        return;
      }
    }

    // Analyze flight range coverage and overlaps
    const {
      uncoveredFlightNumbers,
      overlappingFlightNumbers,
      isFullyCovered,
      hasNoOverlaps
    } = analyzeFlightRangeCoverage(currentSelectedRanges, replacementRanges);

    // Check for uncovered flight numbers
    if (!isFullyCovered) {
      alert(`Missing ranges: ${formatFlightNumberRanges(uncoveredFlightNumbers)}`);
      return;
    }

    // Check for overlaps in replacement ranges
    if (!hasNoOverlaps) {
      alert(`Overlapping ranges: ${formatFlightNumberRanges(overlappingFlightNumbers)}`);
      return;
    }

    setIsConfirmationModalOpen(true);
  };

  return (
    <div className="flight-range-modify">
      <div className="flight-range-modify__header">
        <h1 className="flight-range-modify__title">Modify flight ranges.</h1>
      </div>

      <div className="flight-range-modify__content">
        {/* Form component */}
        <FlightRangeForm
          values={{
            flightRangeStart,
            flightRangeEnd,
            operatingCarrier,
            flightType,
            effectiveDate,
            wetLease
          }}
          onChange={{
            setFlightRangeStart,
            setFlightRangeEnd,
            setOperatingCarrier,
            setFlightType,
            setEffectiveDate,
            setWetLease
          }}
          flightTypeOptions={flightTypeOptions}
          carrierOptions={carrierOptions}
          isLoadingFlightTypes={isLoading}
          onUpdate={handleUpdate}
          onReset={handleReset}
        />

        <div className="flight-range-modify__tables-container">
          {/* Current selected ranges table */}
          <FlightRangeModifyTable
            title="Selected Flight Range(s)"
            ranges={currentSelectedRanges}
            showRemoveButton={true}
            onRemove={handleRemoveRange}
          />

          {/* Replacement ranges table, only shown when there are replacement ranges */}
          {replacementRanges.length > 0 && (
            <FlightRangeModifyTable
              title="Replacement flight(s)"
              ranges={replacementRanges}
              showRemoveButton={true}
              onRemove={handleRemoveReplacementRange}
            />
          )}
        </div>

        {/* Coverage Status Indicator */}
        {(replacementRanges.length > 0 || rangesCoverage.boundsValidationError) && (
          <div className="flight-range-modify__coverage-status">
            <h3 className="flight-range-modify__coverage-title">Flight Range Coverage Status</h3>
            <div className="flight-range-modify__coverage-details">
              <div className={`flight-range-modify__coverage-item ${rangesCoverage.boundsValidationError ? 'error' : (rangesCoverage.isFullyCovered ? 'success' : 'error')}`}>
                <span className="flight-range-modify__coverage-label">Flight Range covered</span>
                <span className="flight-range-modify__coverage-value">
                  {rangesCoverage.boundsValidationError ? '✗ Invalid Range' : (rangesCoverage.isFullyCovered ? '✓ Complete' : '✗ Incomplete')}
                </span>
                {rangesCoverage.boundsValidationError && (
                  <div className="flight-range-modify__coverage-message">
                    <small>{rangesCoverage.boundsValidationError}</small>
                  </div>
                )}
                {!rangesCoverage.boundsValidationError && !rangesCoverage.isFullyCovered && rangesCoverage.uncoveredFlightNumbers.length > 0 && (
                  <div className="flight-range-modify__coverage-message">
                    <small>
                      Missing ranges: {formatFlightNumberRanges(rangesCoverage.uncoveredFlightNumbers)}
                    </small>
                  </div>
                )}
              </div>
              <div className={`flight-range-modify__coverage-item ${rangesCoverage.hasNoOverlaps ? 'success' : 'error'}`}>
                <span className="flight-range-modify__coverage-label">Overlapping Ranges</span>
                <span className="flight-range-modify__coverage-value">
                  {rangesCoverage.hasNoOverlaps ? '✓ None' : '✗ Overlap'}
                </span>
                {!rangesCoverage.hasNoOverlaps && rangesCoverage.overlappingFlightNumbers.length > 0 && (
                  <div className="flight-range-modify__coverage-message">
                    <small>
                      Overlapping ranges: {formatFlightNumberRanges(rangesCoverage.overlappingFlightNumbers)}
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer actions */}
      <FlightRangeFooterActions
        onCancel={handleCancel}
        onSaveDraft={handleSaveDraft}
        onModify={handleModify}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmationModalOpen}
        onClose={() => setIsConfirmationModalOpen(false)}
        onConfirm={handleConfirmModify}
        onEditNotification={handleEditNotification}
        selectedRanges={currentSelectedRanges}
        replacementRanges={replacementRanges}
        notificationData={notificationData}
      />

      {/* Submitting Overlay */}
      {isSubmitting && (
        <div className="flight-range-modify__overlay" aria-live="polite" aria-label="Submitting modifications">
          <div className="flight-range-modify__overlay-content">
            <auro-loader ringworm></auro-loader>
          </div>
        </div>
      )}

      {/* Notification Modal */}
      <NotificationModal
        isOpen={isNotificationModalOpen}
        onClose={() => {
          setIsNotificationModalOpen(false);
          setIsConfirmationModalOpen(false);
        }}
        onConfirmNotification={handleNotificationConfirmed}
        notificationData={notificationData}
      />
    </div>
  );
};

export default FlightRangeModify;